/** @format */
import { getConfig } from '@toucan/common/services/nextjs/settings';

import {
    Market,
    MarketCode,
    MarketLanguage,
} from '@toucan/client/global/market';

import { GetHelpCentreUrlArgs } from './types';

/**
 * Get the help centre URL based on certain factors. These are, in order:
 *   - The `language` property of the `market` argument, when provided
 *   - The `code` property of the `market` argument, when provided
 *   - The `marketLanguage` argument, when provided
 *   - The `marketCode` argument, when provided
 *
 *   If the `isConsolidatedIntercomConfigActive` argument is false, then both
 *   `market.language` and `marketLanguage` will be ignored
 */
export const getHelpCentreUrl = ({
    market,
    marketLanguage,
    marketCode,
    isConsolidatedIntercomConfigActive = false,
}: GetHelpCentreUrlArgs) => {
    const { language, code } = getMarketLanguageAndCode(
        market,
        marketLanguage,
        marketCode
    );

    if (language) {
        const languageHelpUrl = getHelpCentreUrlFromMarketLanguage(
            language,
            isConsolidatedIntercomConfigActive
        );

        if (languageHelpUrl) {
            return languageHelpUrl;
        }
    }

    if (code) {
        return getHelpCentreUrlFromMarketCode(code);
    }

    return '';
};

const getMarketLanguageAndCode = (
    market: Market | undefined,
    marketLanguage: MarketLanguage | undefined,
    marketCode: MarketCode | undefined
) => {
    if (market) {
        const { language, code } = market;
        return { language, code };
    }

    return {
        language: marketLanguage,
        code: marketCode,
    };
};

const getHelpCentreUrlFromMarketLanguage = (
    language: MarketLanguage,
    isConsolidatedIntercomConfigActive: boolean = false
): string | undefined => {
    const { publicRuntimeConfig } = getConfig();
    if (
        !isConsolidatedIntercomConfigActive ||
        !publicRuntimeConfig?.intercomConsolidatedLink
    ) {
        return undefined;
    }
    const baseUrl = new URL(
        language,
        // Want to ensure this ends with a single trailing slash
        `${publicRuntimeConfig.intercomConsolidatedLink}/`.replace(
            /\/{2,}$/,
            '/'
        )
    );
    return baseUrl.href;
};

const getHelpCentreUrlFromMarketCode = (marketCode: MarketCode): string => {
    const { publicRuntimeConfig } = getConfig();

    return publicRuntimeConfig?.intercomLinks?.[marketCode] ?? '';
};
